import React, { useEffect, useState } from 'react';
import { personalTraining } from '../services/api';

// Dashboard operacional do Personal Trainer. PERFORMANCE (financeiro) só quando o backend o devolve
// (Admin autorizado) — o trainer não vê financeiro global.
const fmtTime = (t) => (t ? new Date(t).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' }) : '—');
const STATUS = { Scheduled: ['Agendada', '#2563eb'], Completed: ['Concluída', '#15803d'], Cancelled: ['Cancelada', '#6b7280'], NoShow: ['Falta', '#b91c1c'] };

export default function PtDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [finance, setFinance] = useState(false);

  const load = (inc) => {
    setLoading(true);
    personalTraining.dashboard(inc).then((r) => { setData(r.data); setError(false); })
      .catch((e) => setError(e?.response?.status === 403 ? 'disabled' : 'error'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(false); }, []);

  if (loading) return <div style={S.wrap}><p>A carregar…</p></div>;
  if (error === 'disabled') return <div style={S.wrap}><div style={S.empty}>O módulo Personal Training está desligado neste ginásio.</div></div>;
  if (error || !data) return <div style={S.wrap}><div style={S.empty}>Não foi possível carregar o dashboard PT.</div></div>;

  const today = data.today || {}, portfolio = data.portfolio || {}, perf = data.performance;

  return (
    <div style={S.wrap}>
      <h2 style={S.h2}>Personal Training</h2>

      {/* HOJE */}
      <h3 style={S.h3}>Hoje</h3>
      <div style={S.grid}>
        <Stat label="Sessões" value={today.count ?? 0} />
        <Stat label="Clientes" value={today.clients ?? 0} />
      </div>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr><th style={S.th}>Hora</th><th style={S.th}>Cliente</th><th style={S.th}>Estado</th></tr></thead>
          <tbody>
            {(today.sessions || []).length === 0 && <tr><td colSpan={3} style={S.emptyCell}>Sem sessões hoje.</td></tr>}
            {(today.sessions || []).map((s) => {
              const [lbl, color] = STATUS[s.status] || [s.status, '#374151'];
              return (
                <tr key={s.id}>
                  <td style={S.td}>{fmtTime(s.scheduledAt)}</td>
                  <td style={S.td}><a href={`/admin/members/${s.userId}/360`} style={S.link}>Sócio #{s.userId}</a></td>
                  <td style={S.td}><span style={{ color, fontWeight: 600 }}>{lbl}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* CARTEIRA */}
      <h3 style={S.h3}>Carteira</h3>
      <div style={S.grid}>
        <Stat label="Clientes ativos" value={portfolio.activeClients ?? 0} />
        <Stat label="Pacotes a terminar" value={portfolio.packagesEndingSoon ?? 0} tone={(portfolio.packagesEndingSoon ?? 0) > 0 ? 'warn' : undefined} />
        <Stat label="Sem próxima sessão" value={portfolio.clientsWithoutNextSession ?? 0} tone={(portfolio.clientsWithoutNextSession ?? 0) > 0 ? 'warn' : undefined} />
      </div>

      {/* PERFORMANCE (só quando autorizado) */}
      <h3 style={S.h3}>Performance</h3>
      {perf ? (
        <div style={S.grid}>
          <Stat label="Sessões realizadas" value={perf.completed ?? 0} />
          <Stat label="No-shows" value={perf.noShows ?? 0} />
          <Stat label="Comissão" value={`${Number(perf.commission ?? 0).toFixed(0)}€`} />
        </div>
      ) : (
        <div style={S.perfNote}>
          Indicadores financeiros/comissão só visíveis com autorização.
          <button style={S.ghost} onClick={() => { setFinance(true); load(true); }} disabled={finance}>Mostrar (Admin)</button>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone }) {
  const c = tone === 'warn' ? '#b45309' : '#111827';
  const border = tone === 'warn' ? '#fcd34d' : '#e5e7eb';
  return (
    <div style={{ ...S.stat, borderColor: border }}>
      <div style={{ ...S.statValue, color: c }}>{value}</div>
      <div style={S.statLabel}>{label}</div>
    </div>
  );
}

const S = {
  wrap: { maxWidth: 1000, margin: '0 auto', padding: 16 },
  h2: { fontSize: 24, fontWeight: 800, margin: 0 },
  h3: { fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-secondary)', marginTop: 26 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12, marginTop: 10 },
  stat: { border: '1px solid var(--border-color)', borderRadius: 12, padding: 16, background: 'var(--bg-card)' },
  statValue: { fontSize: 26, fontWeight: 800 },
  statLabel: { color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 },
  tableWrap: { marginTop: 12, overflowX: 'auto', border: '1px solid var(--border-color)', borderRadius: 12 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 420 },
  th: { textAlign: 'left', padding: '10px 12px', color: 'var(--text-secondary)', fontSize: 12, textTransform: 'uppercase', borderBottom: '1px solid var(--border-color)', background: 'var(--info-bg)' },
  td: { padding: '10px 12px', borderBottom: '1px solid var(--border-color)' },
  link: { color: 'var(--info-text)', fontWeight: 600, textDecoration: 'none' },
  emptyCell: { padding: 20, textAlign: 'center', color: 'var(--text-muted)' },
  empty: { color: 'var(--text-muted)', fontSize: 14, padding: 16 },
  perfNote: { marginTop: 10, color: 'var(--text-secondary)', fontSize: 14, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' },
  ghost: { padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'var(--bg-card)', cursor: 'pointer', fontSize: 13 },
};
